// Value picker dispatched on field type — Polish B2.
//
// Keeps visible labels in v2 vocabulary (Account, not Client).
// Multi-select fields render the searchable ``MultiSelect`` (chips
// + "X selected" indicator + searchable, mirrors Mantine's
// MultiSelect). Single-select fields fall through to the native
// ``<select>``.

import {
  useMembersPickerQuery,
  useTagsPickerQuery,
} from "../hooks";
import { useAccountListQuery } from "@/features/accounts/hooks";
import type { FieldDescriptor } from "./fieldRegistry";
import { MultiSelect, type MultiSelectOption } from "./MultiSelect";


export interface ValuePickerProps {
  field: FieldDescriptor;
  value: unknown;
  multi: boolean;
  /** When true the picker is disabled — used on ``is_empty`` /
   * ``is_not_empty`` operators that don't need a value. */
  disabled?: boolean;
  onChange: (next: unknown) => void;
  testId: string;
}


export function ValuePicker({
  field, value, multi, disabled, onChange, testId,
}: ValuePickerProps) {
  if (disabled) {
    return (
      <span
        data-testid={testId}
        className="rounded-full border border-dashed border-border-subtle px-2 py-1 text-xs text-text-tertiary"
      >
        —
      </span>
    );
  }
  switch (field.valueType) {
    case "status":
      return renderMultiOrSingle({
        options: STATUS_OPTIONS, value, multi, onChange, testId,
      });
    case "priority":
      return (
        <SimpleSelect
          options={PRIORITY_OPTIONS}
          value={value}
          onChange={onChange}
          testId={testId}
        />
      );
    case "complexity":
      return (
        <SimpleSelect
          options={COMPLEXITY_OPTIONS}
          value={value}
          onChange={onChange}
          testId={testId}
        />
      );
    case "boolean":
      return (
        <SimpleSelect
          options={BOOLEAN_OPTIONS}
          value={value}
          onChange={(v) => onChange(v === "true" || v === true)}
          testId={testId}
        />
      );
    case "date":
      return (
        <input
          type="date"
          data-testid={testId}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-md border border-border-subtle bg-raised px-2 py-1 text-xs text-text-primary"
        />
      );
    case "text":
      return (
        <input
          type="text"
          data-testid={testId}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type a value…"
          className="rounded-md border border-border-subtle bg-raised px-2 py-1 text-xs text-text-primary"
        />
      );
    case "user":
      return (
        <UserPicker value={value} multi={multi} onChange={onChange} testId={testId} />
      );
    case "tag":
      return (
        <TagPicker value={value} multi={multi} onChange={onChange} testId={testId} />
      );
    case "account":
      return (
        <AccountPicker value={value} multi={multi} onChange={onChange} testId={testId} />
      );
  }
}


const STATUS_OPTIONS: MultiSelectOption[] = [
  { value: "backlog", label: "Backlog" },
  { value: "ready_for_work", label: "Ready" },
  { value: "in_progress", label: "In progress" },
  { value: "in_review", label: "In review" },
  { value: "done", label: "Done" },
  { value: "cancelled", label: "Cancelled" },
];

const PRIORITY_OPTIONS: MultiSelectOption[] = [
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

// T-shirt sizes mapped onto the BE-012 Fibonacci complexity scale.
// X-Large (not "Very Large") so the spread sorts S/M/L/XL the way
// brokers read it.
const COMPLEXITY_OPTIONS: MultiSelectOption[] = [
  { value: "1", label: "Small (1 pt)" },
  { value: "2", label: "Medium (2 pts)" },
  { value: "3", label: "Large (3 pts)" },
  { value: "5", label: "X-Large (5 pts)" },
];

const BOOLEAN_OPTIONS: MultiSelectOption[] = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];


function renderMultiOrSingle({
  options, value, multi, onChange, testId,
}: {
  options: MultiSelectOption[];
  value: unknown;
  multi: boolean;
  onChange: (next: unknown) => void;
  testId: string;
}): JSX.Element {
  if (multi) {
    return (
      <MultiSelect
        options={options}
        value={Array.isArray(value)
          ? value.filter((v): v is string => typeof v === "string")
          : []}
        onChange={onChange}
        testId={testId}
      />
    );
  }
  return (
    <SimpleSelect
      options={options}
      value={value}
      onChange={onChange}
      testId={testId}
    />
  );
}


function SimpleSelect({
  options, value, onChange, testId,
}: {
  options: MultiSelectOption[];
  value: unknown;
  onChange: (next: unknown) => void;
  testId: string;
}) {
  return (
    <select
      data-testid={testId}
      value={typeof value === "string" || typeof value === "number"
        ? String(value)
        : (typeof value === "boolean" ? String(value) : "")}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-border-subtle bg-raised px-2 py-1 text-xs text-text-primary"
    >
      <option value="">Select…</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}


function UserPicker({
  value, multi, onChange, testId,
}: {
  value: unknown;
  multi: boolean;
  onChange: (next: unknown) => void;
  testId: string;
}) {
  const query = useMembersPickerQuery();
  const opts: MultiSelectOption[] = (query.data?.members ?? []).map((m) => ({
    value: m.id,
    label: m.display_name || m.email,
  }));
  return renderMultiOrSingle({
    options: opts, value, multi, onChange, testId,
  });
}


function TagPicker({
  value, multi, onChange, testId,
}: {
  value: unknown;
  multi: boolean;
  onChange: (next: unknown) => void;
  testId: string;
}) {
  const query = useTagsPickerQuery();
  const opts: MultiSelectOption[] = (query.data?.tags ?? []).map((t) => ({
    value: t.id,
    label: t.display_name,
  }));
  return renderMultiOrSingle({
    options: opts, value, multi, onChange, testId,
  });
}


function AccountPicker({
  value, multi, onChange, testId,
}: {
  value: unknown;
  multi: boolean;
  onChange: (next: unknown) => void;
  testId: string;
}) {
  const query = useAccountListQuery({
    filters: {},
    sortBy: "legal_name",
    sortDir: "asc",
    limit: 200,
    offset: 0,
  });
  const opts: MultiSelectOption[] = (query.data?.rows ?? []).map((a) => ({
    value: a.account_id,
    label: a.legal_name,
  }));
  return renderMultiOrSingle({
    options: opts, value, multi, onChange, testId,
  });
}
